import { trackSDK } from '../core';

/**
 * Vue 错误处理桥接。
 *
 * Vue 2 / Vue 3 会在组件相关代码（事件回调、生命周期、watcher、指令钩子等）
 * 内部 try/catch 住错误并交给 Vue.config.errorHandler / app.config.errorHandler
 * 处理，不会重新抛出，因此 window.onerror 收不到。
 *
 * 本 helper 在 Vue 与 SDK 之间建立桥接：把 Vue 捕获到的错误转发给
 * trackSDK.captureError，同时链式保留业务已设置的 errorHandler。
 *
 * 兼容 Vue 2（传入 Vue 构造函数）与 Vue 3（传入 createApp() 返回的 app 实例）。
 */

interface VueLikeConfig {
  errorHandler?: ((err: any, vm: any, info: string) => void) | null;
}

interface VueLikeTarget {
  config: VueLikeConfig;
}

/** 提取 Vue 组件名，兼容 Vue 2 与 Vue 3 的内部结构差异 */
function getComponentName(vm: any): string | undefined {
  if (!vm) return undefined;

  // Vue 2：vm.$options.name / vm.$options._componentTag
  const options = vm.$options;
  if (options) {
    const name = options.name || options._componentTag;
    if (name) return String(name);
  }

  // Vue 3：vm.$.type.name / vm.$.type.__name
  const type = vm.$?.type || vm._?.type;
  if (type) {
    const name = type.name || type.__name;
    if (name) return String(name);
  }

  return undefined;
}

/** 把任意 err 归一化为 Error | string，供 captureError 使用 */
function normalizeError(err: any): Error | string {
  if (err instanceof Error) return err;
  if (typeof err === 'string') return err;
  try {
    return JSON.stringify(err);
  } catch {
    return String(err);
  }
}

/**
 * 为 Vue 2 / Vue 3 安装错误处理桥接。
 *
 * @param target Vue 2 构造函数（`Vue`）或 Vue 3 app 实例（`createApp()` 返回值）
 * @returns 卸载函数，调用后恢复原始 errorHandler
 *
 * @example
 * // Vue 2
 * import Vue from 'vue';
 * import { setupVueErrorHandler } from 'yl-nsm-track';
 * setupVueErrorHandler(Vue);
 *
 * // Vue 3
 * import { createApp } from 'vue';
 * import { setupVueErrorHandler } from 'yl-nsm-track';
 * const app = createApp(App);
 * setupVueErrorHandler(app);
 * app.mount('#app');
 */
export function setupVueErrorHandler(target: any): () => void {
  if (!target || typeof target !== 'object' && typeof target !== 'function') {
    if (typeof console !== 'undefined') {
      console.warn(
        '[yl-nsm-track] setupVueErrorHandler: 传入的参数不是有效的 Vue 构造函数或 app 实例',
      );
    }
    return () => {};
  }

  const config = (target as VueLikeTarget).config;
  if (!config || typeof config !== 'object') {
    if (typeof console !== 'undefined') {
      console.warn(
        '[yl-nsm-track] setupVueErrorHandler: 目标对象没有 config 属性，请确认传入的是 Vue 2 构造函数或 Vue 3 app 实例',
      );
    }
    return () => {};
  }

  // 保留业务已设置的 errorHandler，链式调用
  const previousHandler = config.errorHandler;
  const hadPreviousHandler = typeof previousHandler === 'function';

  const handler = (err: any, vm: any, info: string): void => {
    // 保留 Vue 默认的 console.error 输出。
    // Vue 在 errorHandler 被设置后不会再调用 logError（console.error），
    // 仅当业务未设置自己的 errorHandler 时才补打，避免与业务 handler 重复输出。
    if (
      !hadPreviousHandler &&
      typeof console !== 'undefined' &&
      typeof console.error === 'function'
    ) {
      console.error(err);
    }

    const extra: Record<string, any> = {
      error_type: 'runtime',
      framework: 'vue',
      info,
    };

    const componentName = getComponentName(vm);
    if (componentName) extra.component_name = componentName;

    try {
      trackSDK.captureError(normalizeError(err), extra);
    } catch {
      // captureError 内部已兜底，此处防御性捕获，避免影响 Vue 自身流程
    }

    // 链式调用业务已设置的 handler
    if (hadPreviousHandler) {
      try {
        previousHandler.call(null, err, vm, info);
      } catch {
        // 业务 handler 抛错时不再向上传播，Vue 会另行处理
      }
    }
  };

  config.errorHandler = handler;

  // 返回卸载函数，便于测试 / HMR 场景恢复
  return () => {
    if (config.errorHandler === handler) {
      config.errorHandler = previousHandler;
    }
  };
}
